/**
 * HomeSummaryCard — CCD-DASH-014 Phase A · shared dashboard card contract.
 *
 * Every Home dashboard component (VoyageReadiness, OutstandingActions,
 * UpcomingMilestones, AtAGlanceRail, RecentIntelligence) renders inside
 * this shell. It is the *canonical dashboard component contract* — the
 * traveler recognizes every card by its rhythm, not by ad-hoc styling.
 *
 * Anatomy (top → bottom):
 *   • eyebrow  — one-line uppercase category label
 *   • title    — headline the traveler scans first
 *   • kicker   — small supporting metric (optional, right-aligned)
 *   • body     — arbitrary content slot
 *   • footer   — optional workspace deep-link CTA
 *
 * Never adds emoji or decorative graphics. The card is a *silent frame*
 * for the intelligence inside it.
 */
import React from "react";
import { ArrowRight } from "lucide-react";

const NAVY = "#071e3e";

export function HomeSummaryCard({
  eyebrow,
  title,
  kicker,
  icon,
  children,
  footer,
  footerHref,
  onFooterClick,
  accentColor = NAVY,
  className = "",
  testId,
  size = "md",
}) {
  const padding = size === "sm" ? "p-4" : "p-5";
  return (
    <section
      className={`relative rounded-2xl border border-slate-200 bg-white shadow-sm ${padding} ${className}`}
      data-testid={testId}
    >
      {(eyebrow || kicker) && (
        <div className="flex items-start justify-between gap-3 mb-2">
          {eyebrow && (
            <div
              className="flex items-center gap-1.5 font-body text-[10px] font-semibold uppercase tracking-[0.16em]"
              style={{ color: accentColor }}
            >
              {icon}
              <span>{eyebrow}</span>
            </div>
          )}
          {kicker && (
            <div className="font-body text-[11px] text-slate-500 tabular-nums">
              {kicker}
            </div>
          )}
        </div>
      )}
      {title && (
        <h3 className="font-display text-lg font-semibold text-slate-900 leading-tight tracking-tight">
          {title}
        </h3>
      )}
      {children && <div className="mt-3">{children}</div>}
      {(footer || footerHref || onFooterClick) && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          {onFooterClick ? (
            <button
              type="button"
              onClick={onFooterClick}
              className="inline-flex items-center gap-1.5 font-body text-[12px] font-semibold text-slate-700 hover:text-slate-900 transition-colors"
              data-testid={testId ? `${testId}-footer` : undefined}
            >
              <span>{footer || "Open workspace"}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <div className="font-body text-[12px] text-slate-500">
              {footer}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default HomeSummaryCard;
